import React, { useCallback, useRef, useState } from 'react';
import { Table, message } from 'antd';
import styled from '@emotion/styled';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import {
  ActionsColumn,
  CardNameColumn,
  IndexColumn,
  ShowHideColumn,
  StatusColumn,
  TemplateColumn,
  CardNameProductColumn,
} from './column';
import { HOME } from '../../../constants/define/translation';
import { HomeWidgetsRecord, postSortHomeWidgetsRepository } from '../../../repository/mytm/home/homeWidgetsRepository';
import { HomeType, HomeWidgetStatus } from '../../../types/home';
import { MESSAGE_ERROR } from '../../../constants/message';
import { appStyles } from '../../../styles';

const { Column } = Table;

interface HomeTilesTableProps {
  records: HomeWidgetsRecord[];
  recordsActive: HomeWidgetsRecord[];
  loading?: boolean;
  className?: string;
  isActiveList?: boolean;
  onRefresh: () => void;
  onHandleClickBtnEdit?: (record: HomeWidgetsRecord) => void;
}

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  if (restProps.children[0]?.props?.record?.status === HomeWidgetStatus.ACTIVE) {
    return (
      <tr
        ref={ref}
        className={`${className}${isOver ? dropClassName : ''}`}
        style={{ cursor: 'move', ...style }}
        {...restProps}
      />
    );
  }
  return <tr className={className} style={{ cursor: 'default', ...style }} {...restProps} />;
};

export const HomeTilesTable = (props: HomeTilesTableProps) => {
  const { t } = useTranslation(HOME);
  const [isLoading, setIsLoading] = useState(false);
  const { records, loading, className, isActiveList, onRefresh, onHandleClickBtnEdit, recordsActive } = props;
  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsLoading(true);
      const dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);

      postSortHomeWidgetsRepository({
        sortOrderList: dragRecordsList.map((record, index) => ({
          id: record.id,
          sortOrder: index + 1,
          name: record.name,
          status: record.status,
        })),
      })
        .then(() => {
          setIsLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setIsLoading(false);
        });
    },
    [records, onRefresh],
  );

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledHomeTilesTable
          dataSource={records}
          loading={loading || isLoading}
          className={className}
          rowKey="index"
          components={isActiveList ? components : undefined}
          onRow={
            isActiveList
              ? (recordDeep: any, index?: number): any => {
                  if (recordDeep.status === HomeWidgetStatus.ACTIVE) {
                    return {
                      index,
                      moveRow,
                    };
                  }
                  return {
                    index,
                  };
                }
              : undefined
          }
        >
          <Column
            title={t('table.title.index')}
            align="center"
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
            width="60px"
          />
          {isActiveList && (
            <Column
              title={t('table.title.showHide')}
              align="left"
              dataIndex="show"
              key="show"
              render={(value: any, record: HomeWidgetsRecord) => (
                <ShowHideColumn record={record} onRefresh={onRefresh} />
              )}
              width="80px"
            />
          )}
          <Column
            title={t('table.title.countries')}
            align="center"
            dataIndex="countries"
            key="countries"
            render={name => <CardNameColumn name={name} />}
            width="30%"
          />
          <Column
            title={t('table.title.cardName')}
            align="center"
            dataIndex="name"
            key="name"
            render={(name, title) => <CardNameProductColumn name={name} title={title} />}
            width="30px"
          />
          <Column
            title={t('table.title.template')}
            align="center"
            dataIndex="type"
            key="type"
            render={(type: HomeType) => <TemplateColumn type={type} />}
          />
          <Column
            title={t('table.title.status')}
            align="center"
            dataIndex="status"
            key="status"
            render={(status: HomeWidgetStatus, record: HomeWidgetsRecord) => (
              <StatusColumn status={status} isActive={record?.status === HomeWidgetStatus.ACTIVE} width="120px" />
            )}
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            className="action-home"
            render={(value: any, record: HomeWidgetsRecord) => (
              <ActionsColumn
                record={record}
                onRefresh={onRefresh}
                onHandleClickBtnEdit={onHandleClickBtnEdit}
                recordsActive={recordsActive}
                setLoading={isDataLoad => {
                  setIsLoading(isDataLoad);
                }}
              />
            )}
          />
        </StyledHomeTilesTable>
      </DndProvider>
    </>
  );
};

const StyledHomeTilesTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
